"use client"

import { useState } from "react"
import { InfoAlert } from "src/components/(account)/account/components/InfoAlert"
import { setCountryCode } from "src/components/(checkout)/country/state"
import { CloseButton } from "src/components/common/CloseButton"
import { GenericErrorMessage } from "src/components/common/GenericErrorMessage"
import { Modal } from "src/components/common/Modal"
import { Spinner } from "src/components/icons"
import { CountryFlag } from "src/components/layout/header/CountryFlag"
import { AppCountryCode } from "src/localization/localization"
import { localizeText } from "src/localization/utils"
import { StorefrontCountry } from "src/types/storefront"
import { recordError } from "src/utilities/errorReporting"
import { scrollIntoView } from "src/utilities/scroll"

export type CountrySelectModalProps = {
  close: () => void
  countryCode: AppCountryCode
  countries: StorefrontCountry[]
}

export const CountrySelectModal = ({ close, countryCode, countries }: CountrySelectModalProps) => {
  const [pendingCountryCode, setPendingCountryCode] = useState("")
  const [failed, setFailed] = useState(false)
  const T = localizeText(text)

  async function handleSubmit(country: StorefrontCountry) {
    try {
      setFailed(false)
      setPendingCountryCode(country.code)
      await setCountryCode(country.code)
      close()
      scrollIntoView(document.body, { block: "start" })
    } catch (error) {
      recordError("Failed to change country to " + country.code, error)
      setFailed(true)
    } finally {
      setPendingCountryCode("")
    }
  }

  return (
    <Modal close={close} size="small">
      <Modal.Body>
        <CloseButton onClick={close} />

        <div className="mt-4 flex size-full flex-col items-start justify-center gap-4" inert={!!pendingCountryCode}>
          <ul className="flex flex-col gap-1">
            {countries
              .sort((country) => {
                return country.code === countryCode ? -1 : 1
              })
              .map((country) => (
                <li key={country.code}>
                  <button
                    type="button"
                    onClick={() => handleSubmit(country)}
                    disabled={!!pendingCountryCode}
                    className="flex cursor-pointer items-center gap-x-2 rounded-lg p-2 pr-6 pl-3 hover:bg-gray-100 active:scale-95 active:bg-gray-200 active:shadow-inner"
                  >
                    <CountryFlag country={country} /> {country.name}
                    {pendingCountryCode === country.code && <Spinner className="h-4 w-4" />}
                  </button>
                </li>
              ))}
          </ul>

          {failed && <GenericErrorMessage />}

          <InfoAlert>{T.helpText}</InfoAlert>
        </div>
      </Modal.Body>
    </Modal>
  )
}

const de = {
  close: "Schließen",
  helpText: "Ein Wechsel des Landes kann eine Anpassung der Versandkosten, Preise und Steuern zur Folge haben.",
}

const en: typeof de = {
  close: "Close",
  helpText: "Changing the country may affect shipping costs, prices, and taxes.",
}

const text = { de, en }
